import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, Mail, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { Logo } from "@/components/site/Logo";
import { api, formatApiError } from "@/lib/api";

export default function AdminLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.post("/auth/login", { email, password });
      toast.success("Welcome back, admin");
      navigate("/admin");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0B192C] dot-grid spotlight grain flex items-center justify-center px-5" data-testid="admin-login-page">
      <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className="w-full max-w-md">
        <div className="flex justify-center mb-8"><Logo light /></div>
        <form onSubmit={submit} className="bg-white rounded-[1.75rem] p-8 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.6)]" data-testid="admin-login-form">
          <div className="flex items-center gap-3 mb-7">
            <div className="w-10 h-10 rounded-xl bg-[#0B192C]/5 flex items-center justify-center">
              <ShieldCheck className="w-5 h-5 text-[#0B192C]" />
            </div>
            <div>
              <h1 className="font-bold text-[#0B192C] text-lg">Admin Console</h1>
              <p className="text-xs text-slate-500">Bookings & enquiries management</p>
            </div>
          </div>

          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 text-red-600 text-sm rounded-xl px-4 py-3" data-testid="admin-login-error">
              {error}
            </div>
          )}

          <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Email</label>
          <div className="relative mt-1.5 mb-4">
            <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="admin-login-email"
              className="w-full pl-11 pr-4 py-3 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#0D9488]/40 focus:border-[#0D9488]" placeholder="admin@hemozy.com" />
          </div>

          <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Password</label>
          <div className="relative mt-1.5">
            <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="admin-login-password"
              className="w-full pl-11 pr-4 py-3 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#0D9488]/40 focus:border-[#0D9488]" placeholder="••••••••" />
          </div>

          <button type="submit" disabled={loading} data-testid="admin-login-submit"
            className="mt-6 w-full flex items-center justify-center gap-2 bg-[#D92638] hover:bg-[#B81D2D] text-white font-semibold py-3.5 rounded-full transition-all duration-200 active:scale-[0.98] disabled:opacity-60">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            {loading ? "Signing in..." : "Sign in"}
          </button>
          <div className="mt-5 text-center">
            <Link to="/" className="text-xs text-slate-400 hover:text-[#0D9488] transition-colors" data-testid="back-to-site-link">← Back to hemozy.com</Link>
          </div>
        </form>
      </motion.div>
    </div>
  );
}
